<template>
  <section class="maintenance-page">
    <b-row class="mb-1">
      <b-col cols="12">
        <b-card no-body>
          <b-card-body class="py-1">
            <div class="d-flex flex-wrap align-items-center justify-content-between">
              <div>
                <h3 class="mb-25">{{ $t('Maintenance') }}</h3>
                <div class="text-muted">
                  <span class="mr-1">{{ $t('Model') }}: <strong>{{ asset.modelIdName || '—' }}</strong></span>
                  <span>{{ $t('Identifier') }}: <strong>{{ assetIdentifier || '—' }}</strong></span>
                </div>
              </div>
              <div class="d-flex flex-wrap mt-50 mt-md-0">
                <b-button
                  size="sm"
                  variant="outline-primary"
                  class="mr-50 mb-50 mb-md-0"
                  :to="{ name: 'apps-asset-summary', params: { id: assetId } }"
                >
                  <feather-icon icon="BarChart2Icon" size="14" class="mr-25" />
                  {{ $t('View asset summary') }}
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  :disabled="!assetId"
                  :to="{ name: 'maintenance-record', query: { assetId } }"
                >
                  <feather-icon icon="FilePlusIcon" size="14" class="mr-25" />
                  {{ $t('Register maintenance') }}
                </b-button>
              </div>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="12" md="6" class="mb-1">
        <div class="metric-tile">
          <feather-icon icon="ClockIcon" size="20" class="text-primary mr-1" />
          <div>
            <div class="text-muted small">{{ $t('Hour Meter') }}</div>
            <div class="metric-value">
              {{ formatMetric(currentHourmeter) }} <small>h</small>
            </div>
          </div>
        </div>
      </b-col>
      <b-col cols="12" md="6" class="mb-1">
        <div class="metric-tile">
          <feather-icon icon="NavigationIcon" size="20" class="text-primary mr-1" />
          <div>
            <div class="text-muted small">{{ $t('Odometer') }}</div>
            <div class="metric-value">
              {{ formatMetric(currentOdometer) }} <small>Km</small>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="12" lg="5" class="mb-1">
        <next-maintenance-card
          :next-maintenance="nextMaintenance"
          :has-maintenance-plan="hasMaintenancePlan"
          :loading="isLoading"
          :format-metric="formatMetric"
          @associate="openModalAddPlan"
        />
      </b-col>
      <b-col cols="12" lg="7" class="mb-1">
        <upcoming-maintenances-card
          :items="upcomingMaintenances"
          :overdue-count="overdueCount"
          :loading="isLoading"
          :format-metric="formatMetric"
        />
      </b-col>
    </b-row>

    <b-row class="mb-1">
      <b-col cols="12">
        <associated-plans-card
          :plans="associatedPlans"
          :loading="isLoadingPlans"
          :current-page="currentPage"
          :total-items="totalItems"
          :per-page="perPage"
          :data-meta="dataMeta"
          @add="openModalAddPlan"
          @remove="confirmRemovePlan"
          @update:currentPage="setCurrentPage"
        />
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="12">
        <executed-maintenances-card
          v-if="assetId"
          :asset-id="assetId"
        />
      </b-col>
    </b-row>

    <bind-plan-modal
      :visible="showModal"
      :selected-plan-id="selectedPlanId"
      :options="selectablePlans"
      :binding="isBinding"
      @close="closeModal"
      @bind="bindPlan"
      @update:selectedPlanId="setSelectedPlanId"
    />
  </section>
</template>

<script>
import {
  BButton, BCard, BCardBody, BCol, BRow,
} from 'bootstrap-vue'
import useMaintenance from './useMaintenance'
import NextMaintenanceCard from './components/NextMaintenanceCard.vue'
import UpcomingMaintenancesCard from './components/UpcomingMaintenancesCard.vue'
import AssociatedPlansCard from './components/AssociatedPlansCard.vue'
import ExecutedMaintenancesCard from './components/ExecutedMaintenancesCard.vue'
import BindPlanModal from './components/BindPlanModal.vue'

export default {
  name: 'Maintenance',
  components: {
    BButton,
    BCard,
    BCardBody,
    BCol,
    BRow,
    NextMaintenanceCard,
    UpcomingMaintenancesCard,
    AssociatedPlansCard,
    ExecutedMaintenancesCard,
    BindPlanModal,
  },
  setup() {
    return useMaintenance()
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>

<style lang="scss" scoped>
.metric-tile {
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 0.428rem;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
  padding: 1rem 1.25rem;
  height: 100%;
}

.metric-value {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.2;
}
</style>
